import {
  WellKnownObjects,
  connectionName as wkoConnectionName,
  User,
  Authentication,
} from '@owc/connection-wko-manager';
import { initializeStore, SeamlessCoreStore } from '@seamless/store';
import {
  ApplicationInfos,
  PersistedTradeInState,
  tiConnectionName,
  TradeInDispatchhers,
  TradeInStoreConnection,
} from '@tradein/store';
import { getConfig, getLocale, getUTMParams, parseSrc } from '@tradein/utils';
import { App, computed, ref, Ref } from 'vue';
import { saveMMEValuesToLocalStorage } from '../components/form/utils';
import { trackingService } from '../utils/tracking';
import {
  AUTHENTICATION_CONNECTION,
  AuthenticationState,
  getJwe,
  getUserProfile,
  isPending,
  isLoggedIn,
} from '@b2x/authentication-library';
import {
  AEMComponentPrefill,
  initializeComponentRegistry,
} from '@owc/component-registry-connection';

// @ts-ignore
import {
  MediaQuery,
  MediaQueryChangeEvent,
  MediaQueryService,
} from 'workbench-core';
import i18nManager from './i18nManager';
import { UserProfile } from '../utils/interfaces';

const store: Ref<SeamlessCoreStore> = ref({} as any);
const tradeInStoreConnection: Ref<TradeInStoreConnection> = ref({} as any);
const tradeInStoreDispatchers: Ref<TradeInDispatchhers> = ref({} as any);
const wellKnownObjects: Ref<WellKnownObjects> = ref({} as any);
const configurationId = ref('');
const maintenanceMode = ref(false);
const currentMediaQuery: Ref<MediaQuery> = ref('mq1');
const tradeinId = ref('');
const utmParams = ref('');
const originatingApp = ref('');
const localesLoaded = ref(false);
const applicationInfos: Ref<ApplicationInfos> = ref({} as ApplicationInfos);
const jweToken = ref(undefined as string | undefined);
const userProfile = ref({} as UserProfile | null);
const mediaQueryService = new MediaQueryService(window);
const authPending = ref(true);
const wkoPending = ref(true);
const loggedIn = ref(false);
const componentJson: AEMComponentPrefill | Record<string, unknown> = ref(
  {} as any,
);

const initialized = computed(() => {
  return (
    localesLoaded.value === true &&
    Object.keys(componentJson.value).length > 0 &&
    Object.keys(applicationInfos.value).length > 0 &&
    jweToken.value !== undefined &&
    authPending.value === false &&
    (applicationInfos.value?.meEnabled[configurationId.value]
      ? jweToken.value?.length > 0
        ? authPending.value === false
          ? !!wellKnownObjects.value?.User?.associatedVehicles ||
            !wkoPending.value
          : false
        : authPending.value === false
          ? Object.keys(wellKnownObjects.value).length > 0
          : false
      : true)
  );
});

export default () => {
  const startWorkflow = () => {
    if (initialized.value === true) {
      let once = false;
      if (
        maintenanceMode.value ||
        (applicationInfos.value.applicationMaintenance &&
          applicationInfos.value.applicationMaintenance[
            configurationId.value
          ] === true)
      ) {
        tradeInStoreDispatchers.value.setApplicationMaintenance();
      } else {
        const uri = window.location.search.substring(1);
        const params = new URLSearchParams(uri);
        utmParams.value = getUTMParams() || '';
        tradeinId.value = params.get('tradeinid') || '';
        originatingApp.value =
          params.get('originatingapp')?.toLowerCase() || '';

        tradeInStoreConnection.value.initTradeInApi(
          (applicationInfos.value as ApplicationInfos).apiUrl,
          jweToken.value || '',
        );

        tradeInStoreConnection.value.getApplicationInfos(
          applicationInfos.value as ApplicationInfos,
          tradeInStoreDispatchers.value,
        );

        store.value.subscribe(AUTHENTICATION_CONNECTION, (state) => {
          loggedIn.value = isLoggedIn(state);
        });

        store.value.once(tiConnectionName, (state: PersistedTradeInState) => {
          if (once) return;
          once = true;
          if (
            !state.fromPersistFunction ||
            (tradeinId.value && tradeinId.value !== state.step?.data?.tradeinId)
          ) {
            const mappedMercedesMeVehicles = (
              wellKnownObjects?.value?.User as User
            )?.associatedVehicles?.map((vehicle) => ({
              vin: vehicle.finOrVin,
              imageUrl: vehicle.imageUrl,
              description: vehicle.modelDescription,
              mileage: vehicle.mileage.value || undefined,
            }));

            tradeInStoreConnection.value.initWorkflow(
              configurationId.value,
              tradeinId.value,
              1000,
              tradeInStoreDispatchers.value,
              utmParams.value ? utmParams.value : '',
              { uavList: mappedMercedesMeVehicles },
            );
          }
        });
      }
    }
  };

  const setupStoreConnection = (): Promise<boolean> => {
    return new Promise((res, rej) => {
      store.value = initializeStore() as any;
      tradeInStoreConnection.value = new TradeInStoreConnection();
      return store.value
        .addConnection(tradeInStoreConnection.value as any)
        .then(() => res(true))
        .catch((err) => {
          console.log(err);
          return rej(false);
        });
    });
  };

  const getConnectionDispatchers = (): Promise<boolean> => {
    return new Promise((res, rej) => {
      return store.value
        .getConnectionDispatchers<TradeInDispatchhers>(tiConnectionName)
        .then((dispatchers) => {
          tradeInStoreDispatchers.value = dispatchers;
          res(true);
        })
        .catch(() => {
          rej(false);
        });
    });
  };

  const setupComponentRegistryListener = () => {
    if (window.owc) {
      const componentRegistry = initializeComponentRegistry();
      const componendId = document
        .querySelector('tradein-app')
        ?.getAttribute('component-id');

      if (componendId) {
        componentRegistry.subscribe(
          componendId,
          (compJson: AEMComponentPrefill | Record<string, unknown>) => {
            componentJson.value = { ...compJson };

            const _payload: Record<string, string> = compJson.payload as Record<
              string,
              string
            >;

            if (_payload) {
              if (_payload.configurationid)
                configurationId.value = _payload.configurationid;
              if (_payload.maintenancemode)
                maintenanceMode.value =
                  _payload.maintenancemode === 'true' || false;

              setupConfigurationIdAcceptLanguage();
            }
          },
        );
      }
    } else {
      componentJson.value = { aem: false };

      setupConfigurationIdAcceptLanguage();
    }
  };

  const setupConfigurationIdAcceptLanguage = () => {
    const urlParams = new URLSearchParams(window.location.search);

    configurationId.value =
      urlParams.get('configuration') ||
      window.localStorage.getItem('tradein-configurationid') ||
      configurationId.value ||
      'de-DE-one';

    if (configurationId.value.includes('-mvp')) {
      configurationId.value = configurationId.value.replace('-mvp', '-one');
    }
  };

  const setupWKOListener = () => {
    if (window.aemNamespace) {
      let timeout: ReturnType<typeof setTimeout>;

      store.value.subscribe(
        wkoConnectionName,
        (currWellKnownObjects: WellKnownObjects) => {
          wellKnownObjects.value = { ...currWellKnownObjects };

          if (wellKnownObjects.value.User) {
            trackingService.setUserAssociatedVehicles(
              wellKnownObjects.value.User.associatedVehicles || [],
            );
          }

          clearTimeout(timeout);

          timeout = setTimeout(() => (wkoPending.value = false), 5000); // This is needed as a fallback in case the mercedes me user has an account without any vehicles. We can not rely on checking if the associatedVehicles are set. Best case the associatedVehicles are set from null to [] in the future by the WKO team to get rid of this hacky solutiion.
        },
      );
    } else {
      import('@tradein/utils/src/wko').then((mockWKO) => {
        const storedMeToken = window.localStorage.getItem('tradein-metoken');
        const selectedWKO = window.localStorage.getItem('tradein-wkotype');
        const _mockWKO: WellKnownObjects = (mockWKO.mockTypes.filter(
          (mock) => mock.type === selectedWKO,
        )[0]?.data || mockWKO.mockTypes[0].data) as any as WellKnownObjects;

        if (storedMeToken && _mockWKO && _mockWKO.Authentication) {
          jweToken.value = storedMeToken;

          wellKnownObjects.value = { ..._mockWKO };

          loggedIn.value = true;

          userProfile.value = { lastName1: 'Test' };

          trackingService.setUserAssociatedVehicles(
            (wellKnownObjects.value.User as User).associatedVehicles || [],
          );
        } else {
          wellKnownObjects.value = {
            aem: false,
          } as any as WellKnownObjects;
        }
      });
    }
  };

  const setupAuthenticationListener = () => {
    if (window.aemNamespace) {
      store.value.subscribe(
        AUTHENTICATION_CONNECTION,
        (state: AuthenticationState) => {
          jweToken.value = getJwe(state) || '';
          userProfile.value = getUserProfile(state);
          authPending.value = isPending(state) || false;

          saveMMEValuesToLocalStorage(userProfile.value);
        },
      );
    } else {
      jweToken.value = '';
      authPending.value = false;
    }
  };

  const setupApplicationInfos = (): void => {
    getConfig('tradein-environment', 'tradein-app').then(
      (currApplicationInfos: ApplicationInfos) => {
        applicationInfos.value = { ...currApplicationInfos };
        setupLocale();
      },
    );
  };

  const setApplicationInfos = (data: ApplicationInfos): void => {
    applicationInfos.value = data;
  };

  const setupLocale = (): void => {
    getLocale(applicationInfos.value, configurationId.value).then(
      (messages) => {
        const locale = configurationId.value.substring(0, 5);
        const { i18n } = i18nManager();
        i18n.value.addResourceBundle(locale, 'translation', messages);
        i18n.value.changeLanguage(locale);
        localesLoaded.value = true;
      },
    );
  };

  const setupMediaQueryListener = (): void => {
    currentMediaQuery.value = mediaQueryService.getCurrentMediaQuery();
    mediaQueryService.observe(true);
    window.addEventListener('wbresize', ((
      e: CustomEvent<MediaQueryChangeEvent>,
    ) => {
      currentMediaQuery.value = e.detail.current;
    }) as EventListener);
  };

  const $parseSrc = (src: string) => {
    return parseSrc(src, 'tradein-app');
  };

  const setupErrorHandler = (app: App): void => {
    app.config.errorHandler = (err: any, vm: any, info: any) => {
      console.error('Tradein-Error: ', err, vm, info);
      Object.keys(tradeInStoreDispatchers.value).length > 0 &&
        tradeInStoreDispatchers.value.setApplicationError();
    };
  };

  return {
    store,
    tradeInStoreConnection,
    tradeInStoreDispatchers,
    wellKnownObjects,
    configurationId,
    currentMediaQuery,
    initialized,
    tradeinId,
    utmParams,
    applicationInfos,
    originatingApp,
    userProfile,
    jweToken,
    setupStoreConnection,
    getConnectionDispatchers,
    setupWKOListener,
    setupConfigurationIdAcceptLanguage,
    setupMediaQueryListener,
    startWorkflow,
    setupApplicationInfos,
    setApplicationInfos,
    setupErrorHandler,
    setupAuthenticationListener,
    setupComponentRegistryListener,
    $parseSrc,
    loggedIn,
  };
};
